/**
 * Nightly tenant exports (docs/specs/cloud/tenant-exports.md): each tenant's database rows and the list of its stored
 * files, written every night to the private bucket `exports` so the tenant always holds its own copy. Since decision
 * 838 (carrying out 441, 446 and 702) each export is one AES-256 encrypted zip (opens in 7-Zip) with the passphrase a
 * tenant admin sets, also copied to the tenant's Nextcloud under `<archive folder>/_DB_Backup/<day>/`. This file has
 * the decided numbers, the file layout, the manifest and the words; it has no imports, and an identical copy is
 * `supabase/functions/_shared/tenant-export.ts` (a test fails if they differ), so the Edge Function writes exactly
 * what Teslyn Cloud reads.
 */

/** Finished exports kept per tenant; older ones are removed (as Field's daily database copies, decision 148). */
export const TENANT_EXPORT_KEEP = 14;
/** Rows asked of the database per call. */
export const TENANT_EXPORT_PAGE_ROWS = 2000;
/**
 * Rows per file of a version 1 export (a folder of gzip files, before decision 838), whose larger tables were split
 * into parts so no file neared the bucket's 50 MB limit. Version 2 writes one file per table inside the zip
 * (decision 446: "one JSON-lines file per table").
 */
export const TENANT_EXPORT_PART_ROWS = 100_000;
/** The manifest's format name and version (2: one encrypted zip, decision 446; 1: a folder of gzip files). */
export const TENANT_EXPORT_FORMAT = 'teslyn-tenant-export';
export const TENANT_EXPORT_FORMAT_VERSION = 2;
/** The versions this version of Teslyn reads back (the restore drill reads both). */
export const TENANT_EXPORT_READS = [1, 2] as const;

export type TenantExportMode =
  'tenant' | 'tenant_row' | 'members' | 'client_org' | 'excluded' | 'unclassified';

export interface TenantExportTable {
  readonly table: string;
  readonly mode: TenantExportMode;
  readonly reason: string;
}

/** True for a table whose rows go into the export. */
export function exportsRows(mode: TenantExportMode): boolean {
  return mode === 'tenant' || mode === 'tenant_row' || mode === 'members' || mode === 'client_org';
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const DAY = /^\d{4}-\d{2}-\d{2}$/;
const TABLE = /^[a-z][a-z0-9_]{0,62}$/;

/** tenants/<tenant>/exports/<day>/<export id>: the folder of one export (the database makes the same one). */
export function exportPrefix(tenantId: string, day: string, exportId: string): string {
  if (!UUID.test(tenantId) || !UUID.test(exportId) || !DAY.test(day))
    throw new Error('exportPrefix: bad arguments');
  return `tenants/${tenantId}/exports/${day}/${exportId}`;
}

/** `project.ndjson.gz`, or `tracker_position.part2.ndjson.gz` for the second part of a large table. */
export function exportTableFile(table: string, part = 1): string {
  if (!TABLE.test(table) || !Number.isInteger(part) || part < 1)
    throw new Error('exportTableFile: bad arguments');
  return part === 1 ? `${table}.ndjson.gz` : `${table}.part${part}.ndjson.gz`;
}

/** Version 2: the table's JSON-lines file inside the zip, `project.ndjson` (the zip compresses it). */
export function exportZipTableFile(table: string): string {
  if (!TABLE.test(table)) throw new Error('exportZipTableFile: bad arguments');
  return `${table}.ndjson`;
}

export const EXPORT_FILES_FILE = 'files.ndjson.gz';
/** Version 2: the stored-file list inside the zip. */
export const EXPORT_ZIP_FILES_FILE = 'files.ndjson';
export const EXPORT_MANIFEST_FILE = 'manifest.json';
export const EXPORT_README_FILE = 'README.txt';

const SLUG = /^[a-z0-9][a-z0-9-]{0,62}$/;
const TIME = /^\d{2}:\d{2}$/;

/**
 * The zip's name, the same in Teslyn Cloud and in the Nextcloud: `teslyn-thomson-2026-10-28.zip` for the nightly
 * export, `teslyn-thomson-2026-10-28-1405.zip` for one a tenant admin asked for at 14:05 (Sydney time, as the day),
 * so two on one day never share a name.
 */
export function exportArchiveName(
  slug: string,
  day: string,
  kind: 'nightly' | 'manual',
  localTime: string | null = null,
): string {
  const s =
    slug
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 63) || 'tenant';
  if (!SLUG.test(s) || !DAY.test(day)) throw new Error('exportArchiveName: bad arguments');
  if (kind === 'nightly') return `teslyn-${s}-${day}.zip`;
  if (!localTime || !TIME.test(localTime))
    throw new Error('exportArchiveName: a manual export needs its time');
  return `teslyn-${s}-${day}-${localTime.replace(':', '')}.zip`;
}

/** The local time (hh:mm, Australia/Sydney, as the export day) of a moment, for exportArchiveName. */
export function exportLocalTime(iso: string): string {
  const parts = new Intl.DateTimeFormat('en-AU', {
    timeZone: 'Australia/Sydney',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(iso));
  const get = (t: string): string => parts.find((p) => p.type === t)?.value ?? '00';
  return `${get('hour')}:${get('minute')}`;
}

/** The folder of the backup copies inside the tenant's Nextcloud archive folder (decision 441). */
export const BACKUP_FOLDER = '_DB_Backup';

/**
 * Where a backup's copy goes in the Nextcloud: `<archive folder>/_DB_Backup/<day>/<zip>` (Thomson:
 * `/MBrayne/Teslyn/_DB_Backup/2026-10-28/teslyn-thomson-2026-10-28.zip`).
 */
export function backupCopyPath(folder: string, day: string, archiveName: string): string {
  if (!DAY.test(day) || !/^[a-z0-9][a-z0-9._-]*\.zip$/.test(archiveName))
    throw new Error('backupCopyPath: bad arguments');
  const f = folder.replace(/\/+$/, '');
  return `${f}/${BACKUP_FOLDER}/${day}/${archiveName}`;
}

/** The folders to make inside the archive folder for a day's copy (`_DB_Backup`, then the day). */
export function backupCopyFolders(day: string): readonly string[] {
  if (!DAY.test(day)) throw new Error('backupCopyFolders: bad day');
  return [BACKUP_FOLDER, day];
}

/** A copy that did not read back with the zip's SHA-256 (it is copied again later). */
export const BACKUP_COPY_MISMATCH =
  "The copy in your organisation's Nextcloud did not read back the same as the backup. Teslyn tries again later.";

/** The shortest backup passphrase Teslyn takes (decision 446: a tenant admin sets it). */
export const BACKUP_PASSPHRASE_MIN = 16;
export const BACKUP_PASSPHRASE_MAX = 1024;

export type PassphraseCheck = { readonly ok: true } | { readonly ok: false; readonly message: string };

/**
 * Is a backup passphrase strong enough? The zip's AES-256 key is made from it (WinZip AE-2: PBKDF2 with 1,000
 * rounds), so the backup is only as strong as the passphrase: at least 16 characters with at least 8 different ones.
 * Four or five unrelated words do well, and are easier to type into 7-Zip than random letters.
 */
export function backupPassphraseCheck(passphrase: string, confirm: string = passphrase): PassphraseCheck {
  const p = passphrase;
  if (p.trim().length === 0) return { ok: false, message: 'Type a passphrase.' };
  if (p !== p.trim())
    return { ok: false, message: 'The passphrase starts or ends with a space. Take the space out.' };
  if ([...p].length < BACKUP_PASSPHRASE_MIN)
    return {
      ok: false,
      message: `Use at least ${BACKUP_PASSPHRASE_MIN} characters. Four or five unrelated words work well.`,
    };
  if (p.length > BACKUP_PASSPHRASE_MAX)
    return { ok: false, message: `Use at most ${BACKUP_PASSPHRASE_MAX} characters.` };
  if (new Set([...p.toLowerCase()]).size < 8)
    return { ok: false, message: 'Use more different characters: at least 8 different ones.' };
  if (confirm !== p) return { ok: false, message: 'The two passphrases are not the same.' };
  return { ok: true };
}

export interface ExportFileEntry {
  readonly file: string;
  readonly rows: number;
  readonly bytes: number;
  readonly sha256: string;
}

export interface ExportTableEntry {
  readonly table: string;
  readonly mode: TenantExportMode;
  readonly rows: number;
  readonly files: readonly ExportFileEntry[];
}

/** How a version 2 export is encrypted: AES-256 (WinZip AE-2) with the tenant's passphrase, or not at all. */
export interface ExportEncryption {
  readonly method: 'aes-256' | 'none';
  /** When the passphrase that opens it was set; null when not encrypted. */
  readonly passphrase_set_at: string | null;
}

export interface ExportManifest {
  readonly format: typeof TENANT_EXPORT_FORMAT;
  readonly version: (typeof TENANT_EXPORT_READS)[number];
  readonly export_id: string;
  readonly kind: 'nightly' | 'manual';
  readonly day: string;
  readonly tenant: { readonly id: string; readonly slug: string; readonly name: string };
  readonly started_at: string;
  readonly finished_at: string;
  /** Version 2 only. */
  readonly encryption?: ExportEncryption;
  readonly tables: readonly ExportTableEntry[];
  /** The tenant's stored files (Teslyn Cloud Storage): listed, not copied. */
  readonly stored_files: { readonly count: number; readonly bytes: number; readonly list: ExportFileEntry };
  /** Tables left out, with the reason; `unclassified` ones are a fault to fix in the database. */
  readonly not_exported: readonly {
    readonly table: string;
    readonly mode: TenantExportMode;
    readonly reason: string;
  }[];
}

export interface ExportTotals {
  readonly tables: number;
  readonly rows: number;
  readonly files: number;
  readonly bytes: number;
}

/**
 * What the export record holds: tables written, rows, files written (the manifest and README included), and bytes:
 * the zip's size when there is one (version 2), else the files' sizes added up (version 1).
 */
export function exportTotals(
  m: ExportManifest,
  manifestBytes: number,
  readmeBytes: number,
  archiveBytes: number | null = null,
): ExportTotals {
  const tableFiles = m.tables.flatMap((t) => t.files);
  return {
    tables: m.tables.length,
    rows: m.tables.reduce((n, t) => n + t.rows, 0),
    files: tableFiles.length + 3,
    bytes:
      archiveBytes ??
      tableFiles.reduce((n, f) => n + f.bytes, 0) + m.stored_files.list.bytes + manifestBytes + readmeBytes,
  };
}

/** Read a manifest back (Teslyn Cloud's Exports page, a restore): the shape is checked, not trusted. */
export function parseExportManifest(text: string): ExportManifest {
  const m = JSON.parse(text) as Partial<ExportManifest>;
  if (m.format !== TENANT_EXPORT_FORMAT) throw new Error('This is not a Teslyn export manifest.');
  if (!(TENANT_EXPORT_READS as readonly unknown[]).includes(m.version))
    throw new Error(
      `This export is format version ${String(m.version)}; this version of Teslyn reads versions 1 and 2.`,
    );
  if (!Array.isArray(m.tables) || !m.tenant || typeof m.day !== 'string')
    throw new Error('The export manifest is incomplete.');
  return m as ExportManifest;
}

/** README.txt, written beside the manifest so the folder (version 1) or the zip (version 2) explains itself. */
export function exportReadme(m: ExportManifest): string {
  const rows = m.tables.reduce((n, t) => n + t.rows, 0);
  if (m.version === 2) {
    const enc = m.encryption ?? { method: 'none', passphrase_set_at: null };
    return [
      `Teslyn Cloud backup of ${m.tenant.name} (${m.tenant.slug})`,
      `Day ${m.day}, ${m.kind === 'nightly' ? 'nightly backup' : 'backup asked for by a tenant admin'}, finished ${m.finished_at}.`,
      '',
      enc.method === 'aes-256'
        ? `This zip is encrypted with AES-256. It opens in 7-Zip (or WinZip) with your organisation's backup passphrase, the one set at ${enc.passphrase_set_at ?? 'an unknown time'}.`
        : 'This zip is not encrypted: no backup passphrase was set when it was made. A tenant admin sets one in Teslyn Cloud, Data exports.',
      '',
      `${m.tables.length} tables, ${rows} rows. Each <table>.ndjson file is text with one JSON object per line, one`,
      'line per database row, with the database column names. files.ndjson lists the files stored in Teslyn Cloud',
      '(bucket, path, size, type, last change); the files themselves stay in Teslyn Cloud. Files archived to your',
      'own Nextcloud, photo originals included, are listed in archive_file.ndjson.',
      '',
      'manifest.json lists every file with its row count, size and SHA-256, and the tables left out and why.',
      'Secrets (access keys, app passwords, this passphrase) are never exported: they stay in the encrypted vault.',
      '',
    ].join('\n');
  }
  return [
    `Teslyn Cloud export of ${m.tenant.name} (${m.tenant.slug})`,
    `Day ${m.day}, ${m.kind === 'nightly' ? 'nightly export' : 'export asked for by a tenant admin'}, finished ${m.finished_at}.`,
    '',
    `${m.tables.length} tables, ${rows} rows. Each <table>.ndjson.gz file is gzip-compressed text with one JSON`,
    'object per line, one line per database row, with the database column names. Large tables are split into',
    '<table>.partN.ndjson.gz files. files.ndjson.gz lists the files stored in Teslyn Cloud (bucket, path, size,',
    'type, last change); the files themselves stay in Teslyn Cloud. Files archived to your own Nextcloud are',
    'listed in archive_file.ndjson.gz.',
    '',
    'manifest.json lists every file with its row count, size and SHA-256, and the tables left out and why.',
    'Secrets (access keys, app passwords) are never exported: they stay in the encrypted vault.',
    '',
  ].join('\n');
}

export type ExportCopyStatus = 'none' | 'waiting' | 'copying' | 'done' | 'failed';

/** What the Data exports page says about an export's copy in the tenant's Nextcloud. */
export function exportCopyText(r: {
  readonly status: 'queued' | 'running' | 'done' | 'failed';
  readonly copyStatus: ExportCopyStatus;
  readonly copyPath: string | null;
  readonly copyError: string | null;
  readonly encrypted: boolean | null;
  readonly archiveName: string | null;
}): string {
  if (r.status !== 'done') return '–';
  if (r.archiveName === null) return 'Not copied (made before the Nextcloud copy existed)';
  switch (r.copyStatus) {
    case 'done':
      return `Copied to Nextcloud: ${r.copyPath ?? ''}`.trim();
    case 'waiting':
      return r.copyError ? `Waiting to copy: ${r.copyError}` : 'Waiting to copy to Nextcloud';
    case 'copying':
      return 'Copying to Nextcloud';
    case 'failed':
      return `Not copied: ${r.copyError ?? 'the copy failed'}`;
    case 'none':
    default:
      return r.encrypted === false
        ? 'Not copied: no backup passphrase was set, and an unencrypted backup stays in Teslyn Cloud'
        : 'Not copied: your file archive is Teslyn Cloud, not a Nextcloud';
  }
}

/** "AES-256 (passphrase set 28 Oct 2026)", "Not encrypted", or '–' for a version 1 export. */
export function exportEncryptionText(r: {
  readonly archiveName: string | null;
  readonly encrypted: boolean | null;
  readonly passphraseSetAt: string | null;
}): string {
  if (r.archiveName === null || r.encrypted === null) return '–';
  if (!r.encrypted) return 'Not encrypted';
  if (!r.passphraseSetAt) return 'AES-256';
  const day = new Date(r.passphraseSetAt).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Australia/Sydney',
  });
  return `AES-256 (passphrase set ${day})`;
}

/**
 * Decision 880: while no backup passphrase is set, tenant admins are reminded to set one, on Home and on Data exports.
 * `passphraseSetAt` is tenant_backup's `passphrase_set_at` (null when none is set); `known` is false when Teslyn Cloud
 * could not tell (a database without the backup passphrase yet, or the read failed): then nothing is said. Only tenant
 * admins set the passphrase, so only they are reminded.
 */
export function backupPassphraseReminderDue(
  roles: readonly string[],
  passphraseSetAt: string | null,
  known: boolean,
): boolean {
  return known && passphraseSetAt === null && roles.includes('tenant_admin');
}

/** The reminder's words (decision 880). */
export const BACKUP_PASSPHRASE_REMINDER = {
  title: 'Set the backup passphrase',
  text: 'No backup passphrase is set, so each night’s backup is kept in Teslyn Cloud unencrypted and is not copied to your Nextcloud. Set one so the backups are encrypted and copied.',
  action: 'Set passphrase',
} as const;

/** "1.2 MB", "820 KB", "512 bytes": sizes in the export pages (1024-based, as the archive). */
export function exportSizeText(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
